import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { apiResponse, apiError } from '../schemas/common.schema';

const c = initContract();
const dateish = z.union([z.string(), z.date()]);

export const EventResponseSchema = z.object({
  _id: z.any(),
  event_slug: z.string().optional(),
  event_title: z.string().optional(),
  event_preview: z.string().optional(),
  event_date: dateish.optional(),
  event_cover_image: z.string().optional(),
  event_content: z.any().optional(),
  event_author: z.string().optional(),
  event_locale: z.string().optional(),
  event_views: z.number().optional(),
  event_created_at: dateish.optional(),
  event_modified_at: dateish.optional(),
});

export const getEventsQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().optional(),
  event_locale: z.string().optional(),
  sort_by: z.enum(['event_date', 'created_at', 'modified_at']).optional(),
  sort_order: z.enum(['asc', 'desc']).optional(),
  q: z.string().optional(),
  date_from: z.string().optional(),
  date_to: z.string().optional(),
});

export const createEventBody = z.object({
  event_title: z.string(),
  event_slug: z.string().optional(),
  event_preview: z.string().optional(),
  event_date: z.string(),
  event_cover_image: z.string().optional(),
  event_content: z.any().optional(),
  event_author: z.string().optional(),
  event_locale: z.string().optional(),
});

export const updateEventBody = createEventBody.partial();

// Bare responses (single entity): the API returns the entity directly, not enveloped.
const eventResp = EventResponseSchema;
const viewsResp = z.object({ event_views: z.number() });
const deletedResp = z.object({ deleted: z.boolean() });

export const eventRoutes = {
  getEvents: {
    method: 'GET',
    query: getEventsQuery,
    responses: { 200: apiResponse(z.array(EventResponseSchema)) },
  },
  getEventBySlug: {
    method: 'GET',
    pathParams: z.object({ slug: z.string() }),
    responses: { 200: eventResp, 404: apiError },
  },
  getEventById: {
    method: 'GET',
    pathParams: z.object({ event_id: z.string() }),
    responses: { 200: eventResp, 404: apiError },
  },
  createEvent: {
    method: 'POST',
    body: createEventBody,
    responses: { 200: eventResp },
  },
  updateEvent: {
    method: 'PATCH',
    pathParams: z.object({ event_id: z.string() }),
    body: updateEventBody,
    responses: { 200: eventResp, 404: apiError },
  },
  incrementEventView: {
    method: 'POST',
    pathParams: z.object({ event_id: z.string() }),
    body: z.object({}).optional(),
    responses: { 200: viewsResp, 404: apiError },
  },
  deleteEvent: {
    method: 'DELETE',
    pathParams: z.object({ event_id: z.string() }),
    responses: { 200: deletedResp, 404: apiError },
  },
} as const;

export const eventsContract = c.router({
  getEvents: { ...eventRoutes.getEvents, path: '/events' },
  getEventBySlug: { ...eventRoutes.getEventBySlug, path: '/events/details/:slug' },
  getEventById: { ...eventRoutes.getEventById, path: '/events/:event_id' },
  createEvent: { ...eventRoutes.createEvent, path: '/events' },
  updateEvent: { ...eventRoutes.updateEvent, path: '/events/:event_id' },
  incrementEventView: {
    ...eventRoutes.incrementEventView,
    path: '/events/:event_id/increment-view',
  },
  deleteEvent: { ...eventRoutes.deleteEvent, path: '/events/:event_id' },
});

export type EventResponseDto = z.infer<typeof EventResponseSchema>;
